import './FindSubstituteView.scss';

import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { CallIn } from '@pdcplan/shared/api/TeamPlan';
import EmployeeItem from '../components/EmployeeItem';
import { EmployeeMap } from '../api/model';
import NoCandidates from '../components/NoCandidates';
import ShiftsLoadingState from '../components/ShiftsLoadingState';
import { currentLanguage } from '../currentLanguage';
import { useLocation, useNavigate } from 'react-router-dom';
import { TeamViewMode, getSiteRoutes, getTeamRouteParams } from '../routes';
import { FindSubstituteViewProps } from './FindSubstituteViewContainer';
import { Period } from '../util/dates';
import {
  CreatedShift,
  ShiftDraft,
  ShiftDraftType,
} from '../ListView/CreateShift/EditedShift';

export type StateFromProps = {
  loading: boolean;
  substitutes: CallIn[];
  teamId: string;
  employees: EmployeeMap;
  loadedForShift: string;
  shiftDraft: ShiftDraft | null;
};

export type DispatchFromProps = {
  loadSubstitutes: (
    shiftId: string,
    teamId: string,
    updatedPeriod: Period | null
  ) => void;
  setLoadedForShift: (id: string) => void;
  getEmployees: (personIds: number[], teamId: string) => void;
  getShiftCandidateList: (teamId: string, createdShift: CreatedShift) => void;
};

const FindSubstituteView = ({
  loadedForShift,
  shiftId,
  teamId,
  employees,
  substitutes,
  loading,
  loadSubstitutes,
  setLoadedForShift,
  getEmployees,
  shiftDraft,
  getShiftCandidateList,
}: DispatchFromProps & StateFromProps & FindSubstituteViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrollbarWidth, setScrollbarWidth] = React.useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const fictiveEmployeeIds = useMemo(
    () =>
      Object.values(employees)
        .filter((employee) => employee.isFictive)
        .map((employee) => employee.id),
    [employees]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (ref.current) {
      setScrollbarWidth(ref.current.offsetWidth - ref.current.clientWidth);
    }
  });

  useEffect(() => {
    if (loadedForShift !== shiftId) {
      const period =
        shiftDraft?.type === ShiftDraftType.Edited
          ? { from: shiftDraft.from, to: shiftDraft.to }
          : null;
      if (shiftDraft?.type === ShiftDraftType.Created) {
        getShiftCandidateList(teamId, shiftDraft);
      } else {
        loadSubstitutes(shiftId, teamId, period);
      }
      setLoadedForShift(shiftId);
    }
  }, [
    loadSubstitutes,
    loadedForShift,
    setLoadedForShift,
    shiftId,
    teamId,
    shiftDraft,
    getShiftCandidateList,
  ]);

  const onSubstituteSelect = (personId: number) => {
    navigate(
      getSiteRoutes().team(teamId, {
        ...getTeamRouteParams(location),
        shiftId,
        personId,
        mode: TeamViewMode.FindSubstitute,
      })
    );
  };

  const { assignableSubstitutes, unassignableSubstitutes, fictiveSubstitutes } =
    useMemo(() => {
      const assignableSubstitutes: CallIn[] = [];
      const unassignableSubstitutes: CallIn[] = [];
      const fictiveSubstitutes: CallIn[] = [];
      for (const substitute of substitutes) {
        if (fictiveEmployeeIds.includes(substitute.id)) {
          // Don't show fictive substitutes that are not assignable
          if (substitute.assignable) {
            fictiveSubstitutes.push(substitute);
          }
        } else if (substitute.assignable) {
          assignableSubstitutes.push(substitute);
        } else {
          unassignableSubstitutes.push(substitute);
        }
      }
      return {
        assignableSubstitutes,
        unassignableSubstitutes,
        fictiveSubstitutes,
      };
    }, [substitutes, fictiveEmployeeIds]);

  return (
    <div
      className="findsubstituteview"
      ref={ref}
      style={{ paddingRight: `${Math.max(12 - scrollbarWidth, 0)}px` }}
    >
      {!loading && <div className="list-label">{currentLanguage.callList}</div>}
      {loading ? (
        <ShiftsLoadingState />
      ) : assignableSubstitutes.length < 1 ? (
        <NoCandidates className="empty" />
      ) : (
        assignableSubstitutes.map((substitute) => (
          <EmployeeItem
            key={substitute.id}
            id={substitute.id}
            name={employees[substitute.id]?.name ?? ''}
            status={substitute.shortRule}
            priority={substitute.priority}
            onItemClick={() => onSubstituteSelect(substitute.id)}
          />
        ))
      )}
      {!!fictiveSubstitutes.length && (
        <>
          <div className="list-label">{currentLanguage.FictiveEmployees}</div>
          {fictiveSubstitutes.map((substitute) => (
            <EmployeeItem
              key={substitute.id}
              id={substitute.id}
              name={employees[substitute.id]?.name ?? ''}
              onItemClick={() => onSubstituteSelect(substitute.id)}
              isFictive
              status={substitute.shortRule}
              priority={substitute.priority}
            />
          ))}
        </>
      )}
      {!!unassignableSubstitutes.length && (
        <>
          <div className="list-label">{currentLanguage.OtherEmployees}</div>
          {unassignableSubstitutes.map((substitute) => (
            <EmployeeItem
              key={substitute.id}
              id={substitute.id}
              name={employees[substitute.id]?.name ?? ''}
              status={''}
              priority={substitute.priority}
              onItemClick={() => onSubstituteSelect(substitute.id)}
            />
          ))}
        </>
      )}
    </div>
  );
};
export default FindSubstituteView;

